angular.module('access.Nodes')
    .factory('UploadFolderItem', [
        '$q',
        '$translate',
        'promiseChain',
        'UploadItem',
        'UPLOAD_ITEM_STATUS',
        'UploadFileItem',
        'UploadStat',
        function ($q, $translate, promiseChain, UploadItem, UPLOAD_ITEM_STATUS, UploadFileItem, UploadStat) {
            /**
             * @class UploadFolderItem
             * @classdesc Upload Folder item
             * @extends UploadItem
             * @param {string} name - Folder's name
             */
            function UploadFolderItem(name) {
                /**
                 * @type {UploadFolderItem}
                 */
                var that = this;

                UploadItem.call(this);

                /**
                 * Folder's name
                 * 
                 * @name UploadFolderItem#name
                 * @type {string}
                 * @readonly
                 */
                Object.defineProperty(this, 'name', {
                    enumerable: true,
                    get: function () {
                        return name;
                    }
                });

                /**
                 * This is a directory
                 * 
                 * @name UploadFileItem#isDirectory
                 * @type {boolean}
                 * @readonly
                 */
                Object.defineProperty(this, 'isDirectory', {
                    enumerable: true,
                    value: true,
                    writable: false
                });

                /**
                 * @typedef {object} QueueFolderItem
                 * @property {string} name - Folder's name
                 * @property {string} path - Folder's relative path
                 * @property {Resource} resource - Link to resolved folder's resource
                 * @property {UPLOAD_ITEM_STATUS} status - Status of the item upload
                 */
                /**
                 * @typedef {object} QueueFileItem
                 * @property {UploadFileItem} file - UploadFile instance
                 * @property {QueueFolderItem} folder - link to parent folder item in the queue
                 */

                /**
                 * List of items to upload. Inlcudes all child files/folders and the folder itself
                 * 
                 * @type {Array.<(QueueFolderItem|QueueFileItem)>}
                 */
                var uploadQueue = [{
                    name: name,
                    path: '',
                    resource: null,
                    status: UPLOAD_ITEM_STATUS.NEW
                }];

                /**
                 * Read folder's content
                 * 
                 * @method
                 * @name UploadFolderItem#read
                 * @param {FileSystemDirectoryEntry} entry - Folder's entry to read content from
                 * @param {object} [folderItem=undefined] - Link to the Folder item from items list,
                 *                                     being used to connect nested files with folders
                 * @return {Promise<UploadFolderItem>}
                 */
                Object.defineProperty(this, 'read', {
                    enumerable: false,
                    value: function (entry, folderItem) {
                        return $q(function (resolveFolder) {
                            _readFolder(entry.createReader()).then(function (entries) {
                                var promisesToResolve = [];

                                angular.forEach(
                                    entries,
                                    /**
                                     * @param {FileSystemEntry} entry 
                                     */
                                    function (entry) {
                                        if (entry.isDirectory) {
                                            var newFolderItem = that.addFolder(
                                                entry.name,
                                                entry.fullPath.split('/').slice(1, -1).join('/')
                                            );

                                            promisesToResolve.push(that.read(
                                                entry,
                                                newFolderItem
                                            ));
                                        } else {
                                            /**
                                             * @type {QueueFileItem}
                                             */
                                            var newFileItem = {
                                                file: undefined,
                                                folder: folderItem || uploadQueue[0]
                                            };
                                            uploadQueue.push(newFileItem);

                                            promisesToResolve.push($q(function (resolveFile) {
                                                entry.file(function (file) {
                                                    newFileItem.file = UploadFileItem.fromDrop(entry, file);
                                                    resolveFile();
                                                });
                                            }));
                                        }
                                    }
                                );

                                $q.all(promisesToResolve).then(function () {
                                    resolveFolder(that);
                                });
                            });
                        });
                    }
                });

                /**
                 * Chrome limit the amount of items read by Directory Reader to 100.
                 * In order to give the rest we need to call readEntries again.
                 * This function is a wrapper for reading folder content.
                 * 
                 * @param {FileSystemDirectoryReader} folderReader 
                 * @returns {Promise.<Array.<(FileSystemDirectoryEntry|FileSystemFileEntry)>>}
                 */
                function _readFolder(folderReader, content) {
                    var result = angular.isArray(content) ? content : [];

                    return $q(function (resolve) {
                        folderReader.readEntries(function (entries) {
                            if (entries.length) {
                                Array.prototype.push.apply(result, entries);
                                _readFolder(folderReader, result)
                                    .then(function () {
                                        resolve(result);
                                    });
                            } else {
                                resolve(result);
                            }
                        });
                    });
                }

                /**
                 * Total amount of nested elements
                 * 
                 * @name UploadFolderItem#totalItems
                 * @type {number}
                 */
                Object.defineProperty(this, 'totalItems', {
                    /**
                     * @this {UploadFolderItem}
                     * @returns {number}
                     */
                    get: function () {
                        /**
                         * Don't count the first element in queue as it's folder itself
                         */
                        return uploadQueue.length - 1;
                    }
                });

                /**
                 * Total size in bytes
                 * 
                 * @name UploadFolderItem#totalSize
                 * @type {number}
                 */
                Object.defineProperty(this, 'totalSize', {
                    /**
                     * @this {UploadFolderItem}
                     * @returns {number}
                     */
                    get: function () {
                        return uploadQueue.reduce(function (sum, item) {
                            return sum + (item.file ? item.file.size : 0);
                        }, 0);
                    }
                });

                /**
                 * Amount of uploaded items
                 * 
                 * @name UploadFolderItem#uploadedItems
                 * @type {number}
                 */
                Object.defineProperty(this, 'uploadedItems', {
                    enumerable: true,
                    get: function () {
                        return uploadQueue.filter(function (item, index) {
                            return index > 0 && (
                                item.status === UPLOAD_ITEM_STATUS.SUCCESS ||
                                item.file && item.file.status === UPLOAD_ITEM_STATUS.SUCCESS
                            );
                        }).length;
                    }
                });

                /**
                 * Returns conflicted item name
                 * 
                 * @name UploadFolderItem#conflictName
                 * @type {boolean}
                 */
                Object.defineProperty(this, 'conflictName', {
                    get: function () {
                        for (var i = 0, item = uploadQueue[i]; i < uploadQueue.length; item = uploadQueue[++i]) {
                            if (
                                item.status === UPLOAD_ITEM_STATUS.CONFLICT ||
                                (item.file && item.file.status === UPLOAD_ITEM_STATUS.CONFLICT)
                            ) {
                                var isFolder = angular.isUndefined(item.file);

                                return _getItemFullPath(isFolder ? item : item.file);
                            }
                        }

                        return '';
                    }
                });

                /**
                 * Append file to the folder
                 * 
                 * @method
                 * @name UploadFolderItem#addFile
                 * @param {UploadFileItem} file - Child file
                 * @returns {QueueFileItem} Added file item
                 */
                Object.defineProperty(this, 'addFile', {
                    enumerable: false,
                    value: function (file) {
                        /**
                         * Check if the same file already exists in the list
                         */
                        var fileItem = that.findFile(file.name, file.path);

                        if (fileItem) {
                            return fileItem;
                        }

                        /**
                         * Will recreate the file path in upload queue
                         * and obtain file's parent folder at once
                         */
                        var parentFolder = file.path.split('/').reduce(function (target, folderName, index, paths) {
                            /**
                             * Will skip the first element, as it's a root element which already exists in queue.
                             */
                            if (index === 0) {
                                return target;
                            }

                            var folderPath = paths.slice(0, index).join('/');

                            return (
                                that.findFolder(folderName, folderPath) ||
                                that.addFolder(folderName, folderPath)
                            );
                        }, uploadQueue[0]);

                        var newLength = uploadQueue.push({
                            folder: parentFolder,
                            file: file,
                        });

                        return uploadQueue[newLength - 1];
                    }
                });

                /**
                 * Append folder to the list
                 * 
                 * @method
                 * @name UploadFolderItem#addFolder
                 * @param {string} name - Folder name
                 * @param {string} path - Folder path 
                 * @returns {QueueFolderItem} Added folder item
                 */
                Object.defineProperty(this, 'addFolder', {
                    enumerable: false,
                    value: function (name, path) {
                        var uploadFolderItem = that.findFolder(name, path);

                        if (!uploadFolderItem) {
                            var newLength = uploadQueue.push({
                                name: name,
                                path: path,
                                resource: null,
                                status: UPLOAD_ITEM_STATUS.NEW
                            });
                            uploadFolderItem = uploadQueue[newLength - 1];
                        }

                        return uploadFolderItem;
                    }
                });

                /**
                 * Looking for the file in the uploadQueue
                 * 
                 * @method
                 * @name UploadFolderItem#findFile
                 * @param {string} name - Searching name
                 * @param {string} path - Searching path
                 * @return {(QueueFileItem|undefined)}
                 */
                Object.defineProperty(this, 'findFile', {
                    enumerable: false,
                    value: function (name, path) {
                        return uploadQueue.filter(function (item) {
                            return (
                                item.file &&
                                item.file.name === name &&
                                item.file.path === path
                            );
                        })[0];
                    }
                });

                /**
                 * Looking for the folder in the uploadQueue
                 * 
                 * @method
                 * @name UploadFolderItem#findFolder
                 * @param {string} name - Searching name
                 * @param {string} path - Searching path
                 * @return {(QueueFolderItem|undefined)}
                 */
                Object.defineProperty(this, 'findFolder', {
                    enumerable: false,
                    value: function (name, path) {
                        return uploadQueue.filter(function (item) {
                            return item.name === name && item.path === path;
                        })[0];
                    }
                });

                /**
                 * Upload folder
                 * 
                 * @method
                 * @name UploadFolderItem#upload
                 * @param {Resource} destination - Target folder to upload to
                 * @param {function} onProgress - Callback on uploading progress update
                 * @returns {Promise}
                 */
                Object.defineProperty(this, 'upload', {
                    /**
                     * @this {UploadFolderItem}
                     */
                    value: function (destination, onProgress) {
                        this.status = UPLOAD_ITEM_STATUS.UPLOADING;

                        var currentUploadItem = null;
                        var startTime = Date.now();
                        var bytesTotal = this.totalSize;

                        function _onFileProgress(fileItem, bytesUploaded) {
                            var fileProgress = fileItem.file.progress;
                            var folderProgress = that.progress;
                            var timeSpent = (Date.now() - startTime) / 1000;

                            /**
                             * We can't rely on uploading bytes returned by xhr-request,
                             * cause amount of sent bytes is always bigger than real file size.
                             * We'll use percent value which more or less close to the real state.
                             */
                            folderProgress.bytes = bytesUploaded + Math.round(fileProgress.percent / 100 * fileItem.file.size);

                            var speed = folderProgress.bytes / timeSpent;

                            folderProgress.percent = Math.round(folderProgress.bytes * 100 / bytesTotal);
                            folderProgress.timeLeft = (bytesTotal - folderProgress.bytes) / speed;

                            onProgress();
                        }

                        return promiseChain(uploadQueue.map(function (item) {
                            return function () {
                                currentUploadItem = item;

                                if (item.file) {
                                    return _uploadFile(item, destination, _onFileProgress.bind(that, item, that.progress.bytes));
                                } else {
                                    return _uploadFolder(item, destination);
                                }
                            };
                        }))
                            .then(function () {
                                that.status = UPLOAD_ITEM_STATUS.SUCCESS;
                            })
                            .catch(function (response) {
                                switch (response.status) {
                                    case 409:
                                        currentUploadItem.hasConflict = true;
                                        that.status = UPLOAD_ITEM_STATUS.CONFLICT;
                                        break;

                                    case 0:
                                    case -1:
                                        that.error = new Error($translate.instant('folder_upload_status_failed_message'));
                                        break;

                                    default:
                                        that.error = new Error(
                                            response.data && response.data.error ?
                                                response.data.error.message :
                                                $translate.instant('Error')
                                        );
                                }

                                return $q.reject(response);
                            })
                            .finally(function () {
                                _canceler = null;
                                currentUploadItem = null;
                            });
                    }
                });

                /**
                 * 
                 * @param {object} fileItem - Child file item to upload
                 * @param {Resource} destinationRoot - Target root folder(resource) to upload file to
                 * @param {function} onProgress - Progress callback
                 * @returns {Promise}
                 */
                function _uploadFile(fileItem, destinationRoot, onProgress) {
                    var destination = null;

                    if (fileItem.folder) {
                        destination = $q.resolve(fileItem.folder.resource);
                    } else {
                        /**
                         * Looking for previously uploaded/resolved folder
                         * not to resolve it second time
                         */
                        for (var i = 0, item = uploadQueue[i]; i < uploadQueue.length; item = uploadQueue[++i]) {
                            if (item.name && _getItemFullPath(item) === fileItem.file.path) {
                                destination = item.resource;
                                break;
                            }
                        }
                    }

                    var folderPromise = destination ?
                        $q.resolve(destination) :
                        _resolveFolder(fileItem.file.path, destinationRoot);

                    return folderPromise
                        .then(function (destination) {
                            _canceler = function () {
                                fileItem.file.cancelUpload();
                            };

                            return fileItem.file.upload(destination, onProgress);
                        })
                        .then(function () {
                            _canceler = null;
                        });
                }

                /**
                 * Upload/Create folder
                 * 
                 * @param {QueueFolderItem} folderItem - Folder item to upload
                 * @param {Resource} destinationRoot - Target root resource
                 * @returns {Promise}
                 */
                function _uploadFolder(folderItem, destinationRoot) {
                    var folderFullPath = _getItemFullPath(folderItem);

                    return _resolveFolder(folderFullPath, destinationRoot)
                        .then(function (folderResource) {
                            folderItem.resource = folderResource;
                            folderItem.status = UPLOAD_ITEM_STATUS.SUCCESS;
                        })
                        .catch(function (response) {
                            switch (response.status) {
                                case 409:
                                    folderItem.status = UPLOAD_ITEM_STATUS.CONFLICT;
                                    return $q.reject({
                                        // Mimicry to server error message
                                        data: {
                                            error: {
                                                message: $translate.instant("'%{node_name}' already exists in this location.", {
                                                    node_name: folderFullPath
                                                })
                                            }
                                        }
                                    });

                                default:
                                    folderItem.status = UPLOAD_ITEM_STATUS.ERROR;
                                    return $q.reject(response);
                            }
                        });
                }

                /**
                 * Function attempts to create a folder by that path
                 * 
                 * @param {string} path - Folder path
                 * @param {Resource} destination - Destination resource
                 * @return {Promise}
                 */
                function _resolveFolder(path, destination) {
                    if (path === '') {
                        return $q.resolve(destination);
                    }

                    var destinationPath = (destination.parents || [])
                        .filter(function (parent) { return !parent.is_root; })
                        .map(function (parent) { return parent.name; }).join('/');

                    var request = destination.constructor.createPath({ path: destinationPath + '/' + path, });

                    _canceler = function () {
                        request.$cancelRequest();
                    };

                    return request.$promise.then(function (folderResource) {
                        _canceler = null;
                        return folderResource;
                    });
                }

                /**
                 * Returns item full path (relative + name).
                 * The trick is that we should skip leading and ending slashes.
                 * 
                 * @param {object} item - Item to get full path for
                 * @param {string} item.name - Item's name
                 * @param {string} item.path - Item's path
                 * @returns {string} Item full path
                 */
                function _getItemFullPath(item) {
                    return (item.path ? item.path + '/' : '') + item.name;
                }

                /**
                 * Folder uploading canceler
                 * 
                 * @type {Function}
                 */
                var _canceler = null;

                /**
                 * Cancel current folder uploading
                 * 
                 * @method UploadFolderItem#cancelUpload
                 */
                Object.defineProperty(this, 'cancelUpload', {
                    value: function () {
                        if (this.isUploading && angular.isFunction(_canceler)) {
                            _canceler();
                        }
                    }
                });

                Object.defineProperty(this, 'stat', {
                    get: function () {
                        var stat = new UploadStat();

                        angular.forEach(uploadQueue, function (item) {
                            if (item.file) {
                                stat[item.file.status]++;
                            } else {
                                stat[item.status]++;
                            }
                        });

                        return stat;
                    }
                });
            }

            /**
             * Create an Item from Dropped Folder and read its content
             * 
             * @static
             * @param {FileSystemDirectoryEntry} entry - Entry to create folder from
             * @returns {Promise<UploadFolderItem>}
             */
            UploadFolderItem.fromDrop = function (entry) {
                var folder = new UploadFolderItem(entry.name);
                return folder.read(entry);
            };

            return UploadFolderItem;
        }
    ]);
